// Pure persistence rules shared by DexieRepository and InMemoryRepository, so both enforce
// identical semantics (contracts §10, spec §17).
import type { AttemptRecord, CatalogEntry, SaveOutcome } from './types';

export const MAX_ACTIVE_ATTEMPTS = 20;
export const MAX_HISTORY = 200;
export const LEASE_TTL_MS = 30_000;

/** Typed signal for the 20-active-attempt cap (the archive UI arrives in M3). */
export class ActiveAttemptLimitError extends Error {
  readonly code = 'attempts.limit-reached';
  readonly limit: number;
  constructor(limit: number = MAX_ACTIVE_ATTEMPTS) {
    super(`Active attempt limit reached (${limit})`);
    this.name = 'ActiveAttemptLimitError';
    this.limit = limit;
  }
}

type Failure = Extract<SaveOutcome, { ok: false }>;

/** Is `ownerId` allowed to write given the stored lease at wall time `now`? */
export function leaseAllows(stored: AttemptRecord | undefined, ownerId: string, now: number): boolean {
  const lease = stored?.lease;
  return !lease || lease.ownerId === ownerId || lease.expiresAt <= now;
}

/** Checks lease and revision for a write against the stored record. Null means "go ahead". */
export function checkWrite(
  stored: AttemptRecord | undefined,
  next: AttemptRecord,
  ownerId: string,
  now: number,
): Failure | null {
  // Reopening an archive must pass startAttempt's atomic cap/choice transaction.
  if (stored?.status === 'archived' && next.status !== 'archived')
    return { ok: false, reason: 'stale-revision' };
  if (!leaseAllows(stored, ownerId, now)) return { ok: false, reason: 'lease-lost' };
  if (stored && next.revision <= stored.revision) return { ok: false, reason: 'stale-revision' };
  return null;
}

/** The record as written: the repository, not the caller, owns the lease field. */
export function stamp(next: AttemptRecord, ownerId: string, now: number): AttemptRecord {
  return { ...next, lease: { ownerId, expiresAt: now + LEASE_TTL_MS } };
}

/**
 * Lease acquire/refresh/release. `ttlMs <= 0` releases a lease the caller owns (a clarification
 * of contracts §10 acquireLease, reported as a CCR).
 */
export function nextLease(
  stored: AttemptRecord,
  ownerId: string,
  ttlMs: number,
  now: number,
): { granted: boolean; lease: AttemptRecord['lease'] } {
  if (!leaseAllows(stored, ownerId, now)) return { granted: false, lease: stored.lease };
  if (ttlMs <= 0) return { granted: true, lease: null };
  return { granted: true, lease: { ownerId, expiresAt: now + ttlMs } };
}

/** Maps a storage exception to a SaveOutcome failure reason. */
export function classifyStorageError(err: unknown): 'quota' | 'denied' {
  const name = err instanceof Error ? err.name : '';
  const inner = (err as { inner?: unknown } | null)?.inner;
  const innerName = inner instanceof Error ? inner.name : '';
  const message = err instanceof Error ? err.message : '';
  if (name === 'QuotaExceededError' || innerName === 'QuotaExceededError' || /quota/i.test(message)) {
    return 'quota';
  }
  return 'denied';
}

const DIFFICULTY_RANK: Record<string, number> = { easy: 0, medium: 1, hard: 2 };

/** Catalog order: easy, medium, hard, then level number (numbers restart per difficulty). */
export function compareEntries(a: CatalogEntry, b: CatalogEntry): number {
  return (
    (DIFFICULTY_RANK[a.difficulty] ?? 9) - (DIFFICULTY_RANK[b.difficulty] ?? 9) ||
    a.levelNumber - b.levelNumber
  );
}

/** Meta key holding the puzzle ids a pack imported (written by `importPackBytes`, read by `deletePack`). */
export const packPuzzleIdsKey = (packId: string): string => `catalog.pack.${packId}.puzzleIds`;
